import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../../services/api";
import Avatar from "../../components/Avatar/Avatar";
import styles from "./FollowRequests.module.scss";

interface FollowRequest {
  requestId: string;
  requesterId: string;
  requesterUsername: string;
  requesterAvatarUrl?: string;
}

const PAGE_SIZE = 20;

const FollowRequests: React.FC = () => {
  const navigate = useNavigate();
  const [requests, setRequests] = useState<FollowRequest[]>([]);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [actionInProgress, setActionInProgress] = useState<string | null>(null);

  const fetchRequests = useCallback(async (pageNum: number) => {
    setLoading(true);
    try {
      const data = await api.social.listFollowRequests(pageNum);
      if (pageNum === 1) {
        setRequests(data.requests);
      } else {
        setRequests((prev) => [...prev, ...data.requests]);
      }
      setTotal(data.total);
    } catch (error) {
      console.error("Failed to fetch follow requests:", error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchRequests(1);
  }, [fetchRequests]);

  const handleApprove = async (requestId: string) => {
    setActionInProgress(requestId);
    try {
      await api.social.approveFollowRequest(requestId);
      setRequests((prev) => prev.filter((r) => r.requestId !== requestId));
      setTotal((prev) => prev - 1);
    } catch (error) {
      console.error("Failed to approve follow request:", error);
    } finally {
      setActionInProgress(null);
    }
  };

  const handleReject = async (requestId: string) => {
    setActionInProgress(requestId);
    try {
      await api.social.rejectFollowRequest(requestId);
      setRequests((prev) => prev.filter((r) => r.requestId !== requestId));
      setTotal((prev) => prev - 1);
    } catch (error) {
      console.error("Failed to reject follow request:", error);
    } finally {
      setActionInProgress(null);
    }
  };

  const handleLoadMore = () => {
    const nextPage = page + 1;
    setPage(nextPage);
    fetchRequests(nextPage);
  };

  const hasMore = requests.length < total;

  if (loading && requests.length === 0) {
    return (
      <div className={styles.page}>
        <h1 className={styles.title}>Follow Requests</h1>
        <div className={styles.loading}>Loading...</div>
      </div>
    );
  }

  return (
    <div className={styles.page}>
      <h1 className={styles.title}>Follow Requests</h1>

      {requests.length === 0 ? (
        <div className={styles.empty}>No pending follow requests</div>
      ) : (
        <>
          <div className={styles.list}>
            {requests.map((request) => (
              <div key={request.requestId} className={styles.requestItem}>
                <div className={styles.userInfo}>
                  <Avatar
                    src={request.requesterAvatarUrl || ""}
                    username={request.requesterUsername}
                    size="md"
                    onClick={() => navigate(`/profile?user=${request.requesterId}`)}
                  />
                  <span className={styles.username}>
                    {request.requesterUsername}
                  </span>
                </div>
                <div className={styles.actions}>
                  <button
                    className={styles.approveBtn}
                    onClick={() => handleApprove(request.requestId)}
                    disabled={actionInProgress === request.requestId}
                  >
                    Approve
                  </button>
                  <button
                    className={styles.rejectBtn}
                    onClick={() => handleReject(request.requestId)}
                    disabled={actionInProgress === request.requestId}
                  >
                    Reject
                  </button>
                </div>
              </div>
            ))}
          </div>

          {hasMore && (
            <button
              className={styles.loadMore}
              onClick={handleLoadMore}
              disabled={loading}
            >
              {loading ? "Loading..." : "Load More"}
            </button>
          )}
        </>
      )}
    </div>
  );
};

export default FollowRequests;
